import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'
import WhyStriveSection from '@/components/home/WhyStriveSection'
import StatsSection from '@/components/home/StatsSection'
import FinalCTA from '@/components/home/FinalCTA'

const lineV = {
  hidden: { y: '115%' },
  visible: (i) => ({
    y: '0%',
    transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1], delay: 0.05 + i * 0.1 },
  }),
}

const IMAGE_URL = 'https://images.pexels.com/photos/32977153/pexels-photo-32977153.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'

const values = [
  {
    label: 'The question we ask',
    body: 'Not "who is available?" but "who has actually done this — at this scale, with this complexity?" That question is harder to answer. It is also the only one worth asking.',
  },
  {
    label: 'The market we know',
    body: 'We have spent years mapping the technology talent landscape in Bengaluru, Hyderabad, Pune, and Mumbai — not from a database, but from direct relationships built over hundreds of conversations with senior professionals.',
  },
  {
    label: 'The standard we hold',
    body: 'Every search is retained and run to completion. Every shortlist is assessed with the same Four-Factor rigour regardless of seniority. We do not present names. We present evidence.',
  },
]

export default function AboutPage() {
  return (
    <main data-testid="about-page">
      <Navbar />

      {/* Hero */}
      <section className="min-h-[70vh] bg-[#0A0A0A] flex flex-col justify-end px-6 md:px-12 lg:px-24 pb-20 pt-36 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-px bg-white/5 hidden lg:block" />
        <motion.div initial="hidden" animate="visible" className="max-w-7xl relative z-10">
          <div className="overflow-hidden mb-6">
            <motion.p custom={0} variants={lineV}
              className="text-[10px] tracking-[0.38em] uppercase font-body font-semibold text-white/30">
              About Strive Partners
            </motion.p>
          </div>
          <h1 className="mb-8">
            <div className="overflow-hidden">
              <motion.div custom={1} variants={lineV}
                className="text-[clamp(3rem,7vw,7.5rem)] font-heading font-black tracking-[-0.03em] leading-none text-white">
                Built around
              </motion.div>
            </div>
            <div className="overflow-hidden">
              <motion.div custom={2} variants={lineV}
                className="text-[clamp(3rem,7vw,7.5rem)] font-heading font-black tracking-[-0.03em] leading-none text-white">
                the question every
              </motion.div>
            </div>
            <div className="overflow-hidden">
              <motion.div custom={3} variants={lineV}
                className="text-[clamp(3rem,7vw,7.5rem)] font-heading font-light tracking-[-0.03em] leading-none text-white/35">
                GCC eventually asks.
              </motion.div>
            </div>
          </h1>
          <div className="overflow-hidden">
            <motion.p custom={4} variants={lineV}
              className="text-base md:text-lg text-white/45 max-w-xl font-body leading-relaxed">
              Not "who is available?" — but "who has actually done this, at this scale, with this complexity?"
            </motion.p>
          </div>
        </motion.div>
      </section>

      {/* Story section */}
      <section
        data-testid="about-story-section"
        className="py-32 md:py-40 px-6 md:px-12 lg:px-24 bg-white"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            {/* Left: Image */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
              className="relative h-[360px] md:h-[480px] overflow-hidden bg-[#F6F7F9]"
            >
              <img
                src={IMAGE_URL}
                alt="Strive Partners — editorial"
                className="w-full h-full object-cover object-center grayscale"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-[#0B1F3A]/15" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <p className="text-[9px] tracking-[0.3em] uppercase text-white/60 font-body font-semibold">
                  Bengaluru, India
                </p>
              </div>
            </motion.div>

            {/* Right: Story */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
            >
              <p className="text-[10px] tracking-[0.35em] uppercase text-[#0A0A0A]/30 mb-8 font-body font-semibold">
                The Firm
              </p>
              <p className="text-2xl md:text-3xl font-heading font-black text-[#0A0A0A] leading-tight tracking-tight mb-8">
                We started Strive Partners because we saw the gap between what GCC leaders needed and what most search firms could provide.
              </p>
              <p className="text-sm md:text-base font-body text-[#0A0A0A]/55 leading-relaxed mb-6">
                Strive Partners was founded by professionals who have led talent acquisition inside some of the most demanding technology organisations globally. We understand both sides of the table — what it takes to hire well, and what it takes to be hired well.
              </p>
              <p className="text-sm font-body text-[#0A0A0A]/55 leading-relaxed mb-10">
                We built this firm around the conviction that search quality is determined by the brief interrogation, not the database size. Ask better questions. Map the market properly. Approach the right people directly. The rest follows.
              </p>
              <Link
                to="/contact"
                data-testid="about-cta"
                className="inline-flex items-center gap-3 text-[10px] font-body font-semibold tracking-[0.18em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A]/30 pb-0.5 hover:text-[#0B1F3A] hover:border-[#0B1F3A] transition-colors"
              >
                Start a conversation <ArrowRight size={11} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our values */}
      <section
        data-testid="about-values-section"
        className="py-32 md:py-40 px-6 md:px-12 lg:px-24 bg-[#F6F7F9]"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mb-20"
          >
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#0A0A0A]/30 mb-5 font-body font-semibold">
              What Drives Us
            </p>
            <h2 className="text-[clamp(2.5rem,5vw,5rem)] font-heading font-black tracking-tighter leading-none text-[#0A0A0A]">
              Three things we<br />
              <span className="font-light text-[#16365F]">will not compromise on.</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-[#E5E7EB]">
            {values.map((v, i) => (
              <motion.div
                key={v.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 }}
                className={`py-12 ${i < 2 ? 'md:pr-12 md:border-r border-[#E5E7EB]' : ''} ${i > 0 ? 'md:pl-12' : ''} border-b md:border-b-0 border-[#E5E7EB]`}
                data-testid={`about-value-${i + 1}`}
              >
                <p className="text-[9px] tracking-[0.3em] uppercase text-[#0A0A0A]/20 font-body font-semibold mb-5">
                  {String(i + 1).padStart(2, '0')}
                </p>
                <h3 className="text-base md:text-lg font-heading font-black text-[#0A0A0A] mb-4 leading-tight tracking-tight">
                  {v.label}
                </h3>
                <p className="text-sm font-body text-[#0A0A0A]/55 leading-relaxed">
                  {v.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <WhyStriveSection />
      <StatsSection />
      <FinalCTA />
      <Footer />
    </main>
  )
}
